import {StateCreator} from "zustand/vanilla";
import {ThreadEntity} from "@/pb/api/devplan/types/threads";
import {AIMessageDetails, Message, MessageMetadata, MessageToken} from "@/pb/api/devplan/types/ai";

export interface StateUpdate<T> {
  key: ThreadEntity
  update: (s: T) => T
  ephemeral?: boolean
}

export type AgentStoreState = {
  agentStates: Record<string, unknown>;
  ephemeralAgentStates: Record<string, unknown>;
  messageIds: Record<string, string[]>;
  messages: Record<string, Message>;
  setAgentState(key: ThreadEntity, state: unknown): void;
  getAgentState<T>(key: ThreadEntity): T | undefined;
  updateState<T>(update: StateUpdate<T>): void;
  updateMessage(key: ThreadEntity, id: string, update: (m: Message) => Message): void;
  appendToken(key: ThreadEntity, token: MessageToken): void;
  appendMessage(key: ThreadEntity, msg: Message): void;
  removeLastAiMessages(key: ThreadEntity): void;
  updateMessages(key: ThreadEntity, messages: Message[]): void;
  cleanThreadActivity(key: ThreadEntity): void;
}

export const createAgentSlice: StateCreator<
  AgentStoreState,
  [],
  [],
  AgentStoreState
> = ((set, get) => ({
  agentStates: {},
  ephemeralAgentStates: {},
  messageIds: {},
  messages: {},
  setAgentState: (key: ThreadEntity, state: unknown) => set(
    s => ({...s, agentStates: {...s.agentStates, [getStateKey(key)]: state}})
  ),
  getAgentState<T>(key: ThreadEntity): T | undefined {
    const res = get().agentStates[getStateKey(key)]
    return res ? res as T : undefined
  },
  updateState<T>({key, update, ephemeral}: StateUpdate<T>) {
    set(s => {
      const stateKey = ephemeral ? "ephemeralAgentStates" : "agentStates"
      const keyStr = getStateKey(key)
      let st = s[stateKey][keyStr]
      if (!st && ephemeral) {
        st = {}
      }
      if (!st) {
        return s
      }
      return {...s, [stateKey]: {...s[stateKey], [keyStr]: update(st as T)}}
    })
  },
  appendMessage(key: ThreadEntity, msg: Message) {
    set(s => {
      const stateKey = getStateKey(key)
      const curIds = s.messageIds[stateKey] ?? []
      const newMessageIds = curIds.includes(msg.id) ? curIds : [...curIds, msg.id]
      return {
        ...s,
        messageIds: {...s.messageIds, [stateKey]: newMessageIds},
        messages: {...s.messages, [getMessageKey(key, msg.id)]: msg},
      }
    })
  },
  removeLastAiMessages: key => set(s => {
    const stateKey = getStateKey(key)
    const messageIds = s.messageIds[stateKey] ?? []
    if (messageIds.length === 0) {
      return s
    }
    const toDelete: string[] = []
    for (const id of messageIds.toReversed()) {
      const msg = s.messages[getMessageKey(key, id)]
      if (!msg || msg.details?.$case !== "ai") {
        break
      }
      toDelete.push(msg.id)
    }
    if (toDelete.length === 0) {
      return s
    }
    const newMessageIds = messageIds.filter(id => !toDelete.includes(id))
    return {
      ...s,
      messageIds: {...s.messageIds, [stateKey]: newMessageIds},
    }
  }),
  updateMessages(key: ThreadEntity, messages: Message[]) {
    set(s => {
      const stateKey = getStateKey(key)
      const newMessageIds = getUpdatedMessageIds(s.messageIds[stateKey] ?? [], messages.map(m => m.id))
      const messageUpdates: Record<string, Message> = {}
      for (const m of messages) {
        const k = getMessageKey(key, m.id)
        const cur = s.messages[k]
        if (!cur) {
          messageUpdates[k] = m
        } else if (cur.content !== m.content || !cur.meta) {
          messageUpdates[k] = {...cur, content: m.content, meta: m.meta ?? cur.meta }
        }
      }
      return {
        ...s,
        messageIds: {...s.messageIds, [stateKey]: newMessageIds},
        messages: {...s.messages, ...messageUpdates},
      }
    })
  },
  updateMessage(key: ThreadEntity, id: string, update: (m: Message) => Message) {
    return set(s => {
      const msgKey = getMessageKey(key, id)
      const msg = s.messages[msgKey]
      if (!msg) {
        return s
      }
      return {...s, messages: {...s.messages, [msgKey]: update(msg)}}
    })
  },
  appendToken: (key: ThreadEntity, token: MessageToken) => set(s => {
    const stateKey = getStateKey(key)
    const msgKey = getMessageKey(key, token.messageId)
    const curIds = s.messageIds[stateKey] ?? []
    const shouldAppendToIds = !curIds.includes(token.messageId)
    const cur = s.messages[msgKey]
    const newMessage = cur ? {...cur, content: `${cur.content}${token.token}`} : Message.create({
      id: token.messageId, content: token.token, meta: MessageMetadata.create({createdAt: new Date()}),
      details: {$case: "ai", value: AIMessageDetails.create({})}
    })
    const newMessageIds = shouldAppendToIds ? [...curIds, token.messageId] : curIds
    return {
      ...s,
      messageIds: {...s.messageIds, [stateKey]: newMessageIds},
      messages: {...s.messages, [msgKey]: newMessage},
    }
  }),
  cleanThreadActivity: (key) => set(s => {
    const stateKey = getStateKey(key)
    const curIds = s.messageIds[stateKey] ?? []
    const toDelete = new Set(
      curIds.filter(id => s.messages[getMessageKey(key, id)]?.details?.$case === "threadProgress")
    )
    if (toDelete.size === 0) {
      return s
    }
    return {
      ...s,
      messageIds: {...s.messageIds, [stateKey]: curIds.filter(id => !toDelete.has(id))},
      messages: Object.fromEntries(Object.entries(s.messages).filter(([id]) => !toDelete.has(id)))
    }
  })
}))

export function getStateKey(k: ThreadEntity): string {
  return `${k.entityId}|${k.entityType}|${k.agentType}|${k.companyId}`
}

export function getMessageKey(k: ThreadEntity, id: string) {
  return `${getStateKey(k)}|${id}`
}

function getUpdatedMessageIds(currentIds: string[], newIds: string[]) {
  const seen = new Set(currentIds)
  return [...currentIds, ...newIds.filter(id => !seen.has(id))]
}
